import {
  AfterViewInit,
  Directive,
  DoCheck,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges
} from '@angular/core';

@Directive({
  selector: 'input[rnplEllipsisPlaceholder]'
})
export class EllipsisPlaceholderDirective implements AfterViewInit, DoCheck, OnChanges, OnDestroy {

  @Input() rnplEllipsisPlaceholder: string;

  private placeholderElement: HTMLElement;
  private placeholderTextElement: HTMLElement;
  private parentElement: HTMLElement;
  private parentPositionChanged: boolean = false;
  private previousValue: string;

  constructor(
    private readonly elementRef: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    const input = this.elementRef.nativeElement;
    this.parentElement = input.parentElement;

    if (!this.parentElement) { return; }

    if (window.getComputedStyle(this.parentElement).position === 'static') {
      this.renderer.setStyle(this.parentElement, 'position', 'relative');
      this.parentPositionChanged = true;
    }

    this.placeholderElement = this.renderer.createElement('span');
    this.placeholderTextElement = this.renderer.createElement('span');
    this.renderer.addClass(this.placeholderElement, 'rnpl-ellipsis-placeholder');
    this.renderer.addClass(this.placeholderTextElement, 'rnpl-ellipsis-placeholder-text');
    this.renderer.appendChild(this.placeholderElement, this.placeholderTextElement);
    this.renderer.appendChild(this.parentElement, this.placeholderElement);
    this.updatePlaceholderText();
    this.updateVisibility();
    setTimeout(() => this.updatePosition());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rnplEllipsisPlaceholder) {
      this.updatePlaceholderText();
    }
  }

  ngDoCheck(): void {
    if (!this.placeholderElement) { return; }

    const value = this.elementRef.nativeElement.value;
    if (value !== this.previousValue) {
      this.updateVisibility();
    }
  }

  @HostListener('input')
  @HostListener('change')
  public updateVisibility(): void {
    if (!this.placeholderElement) { return; }

    this.previousValue = this.elementRef.nativeElement.value;
    this.renderer.setStyle(this.placeholderElement, 'display', this.previousValue ? 'none' : 'flex');
  }

  @HostListener('window:resize')
  public updatePosition(): void {
    if (!this.placeholderElement || !this.parentElement) { return; }

    const input = this.elementRef.nativeElement;
    const inputRect = input.getBoundingClientRect();
    const parentRect = this.parentElement.getBoundingClientRect();
    const inputStyles = window.getComputedStyle(input);
    const paddingLeft = parseFloat(inputStyles.paddingLeft) || 0;
    const paddingRight = parseFloat(inputStyles.paddingRight) || 0;

    this.renderer.setStyle(this.placeholderElement, 'top', `${inputRect.top - parentRect.top}px`);
    this.renderer.setStyle(this.placeholderElement, 'right', `${parentRect.right - inputRect.right + paddingRight}px`);
    this.renderer.setStyle(this.placeholderElement, 'left', `${inputRect.left - parentRect.left + paddingLeft}px`);
    this.renderer.setStyle(this.placeholderElement, 'height', `${inputRect.height}px`);
    this.renderer.setStyle(this.placeholderElement, 'font-size', inputStyles.fontSize);
    this.renderer.setStyle(this.placeholderElement, 'font-weight', inputStyles.fontWeight);
    this.renderer.setStyle(this.placeholderElement, 'letter-spacing', inputStyles.letterSpacing);
  }

  ngOnDestroy(): void {
    if (this.placeholderElement && this.parentElement) {
      this.renderer.removeChild(this.parentElement, this.placeholderElement);
    }

    if (this.parentPositionChanged && this.parentElement) {
      this.renderer.removeStyle(this.parentElement, 'position');
    }
  }

  private updatePlaceholderText(): void {
    this.renderer.setAttribute(
      this.elementRef.nativeElement,
      'aria-placeholder',
      this.rnplEllipsisPlaceholder || ''
    );

    if (this.placeholderTextElement) {
      this.renderer.setProperty(this.placeholderTextElement, 'textContent', this.rnplEllipsisPlaceholder || '');
    }
  }
}
